import { Injectable, Injector, inject } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { AppGlobalsModule } from 'src/app/app-globals.module';
import { I18nService } from '../i18n/i18n.service';
import { REQUEST } from 'src/express.tokens';
import { NihulModeService } from 'src/app/nihul/nihul-mode.service';

/** the site's own public apis (relative urls only - an absolute url is another system's) */
const PUBLIC_API_URL = /^(\/(midrash|ask|article))?\/api\//i;

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
    private _i18n = inject(I18nService);
    private _injector = inject(Injector);
    private _request = inject(REQUEST, { optional: true });
    private _nihulMode = inject(NihulModeService);

    intercept(request: HttpRequest<any>, next: HttpHandler) {
        //make lowercase if it would be a 301 by varnish
        let url = request.url;
        let regexp = new RegExp('^\\/(midrash|ask)/api', 'i');
        // let params = request.params;

        if (regexp.test(request.url)) {
            url = url.toLowerCase();
        }

        // Clone to add request header for language
        let headers = request.headers.set('Lang', this._i18n.getLang());

        //a read made by an internal editing screen: mark it so nothing between here and the
        //server hands back a copy older than the editor's own save (NihulModeService explains
        //why the browser cache is the one that needs this). Varnish strips the marker before it
        //hashes, so the nihul still shares the public site's cache object. Added as a PARAM, not
        //concatenated: HttpParams are appended after the url's own query, so the marker always
        //ends up LAST. A cdn keys on the EXACT url, so the still-open purge of the marked cdn
        //copy (cache-flow.md item 8 - it must be built in the flush endpoint, varnish strips the
        //marker) has to produce this same marker-last form to hit the same cached object.
        //Lowercase, since the url above may have been lowercased.
        let params = request.params;
        if (!AppGlobalsModule.isServer && request.method === 'GET' && this._nihulMode.active() && PUBLIC_API_URL.test(url)) {
            params = params.append('nihul', '1');
            //nor from the in-process request cache - CachingInterceptor runs after this one
            headers = headers.set('noCache', '1');
        }

        //in server - use only http
        if (AppGlobalsModule.isServer) {
            let defaultReferer = 'http://main.internal.yeshiva.org.il';
            let referer = defaultReferer;
            let userAgent = 'angular universal';
            headers = headers.set('httponly', 'true');

            try {
                const tempUa = this._getServerHeader('user-agent');
                if (tempUa) {
                    userAgent = tempUa;
                } else {
                    const tempUaFake = this._getServerHeader('user-agent-fake');
                    if (tempUaFake) {
                        userAgent = tempUaFake;
                    }
                }
                headers = headers.set('user-agent-fake', userAgent);
            } catch (error) {}
            try {
                let tempReferer = this._getServerHeader('original-url');
                if (tempReferer) {
                    referer += tempReferer;
                }
            } catch (error) {
                referer = defaultReferer;
            }
            headers = headers.set('from-angular', '1');

            let host = this._getServerHeader('host');
            if (host && (host.toLowerCase().startsWith('direct') || host.toLowerCase().includes('local'))) {
                headers = headers.set('isDirect', '1');
            }
            if (referer) {
                headers = headers.set('referer-fake', referer);
            }
            try {
                // In development we might not have x-forwarded-for
                if (!AppGlobalsModule.isDevelopment) {
                    let forwardedIp = this._getServerHeader('x-forwarded-for');
                    let regexXForwardedFor = new RegExp('^[0-9., ]+$');
                    if (forwardedIp && regexXForwardedFor.test(forwardedIp)) {
                        // we send the original x-forwarded-for in a different header
                        // so servers can use it for logging and other things,
                        // but we do not forward it to the backend, so it doesn't get blocked by varnish and other things that
                        // might block x-forwarded-for
                        headers = headers.set('x-forwarded-for-original-request', forwardedIp);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }

        const newReq = request.clone({ headers: headers, url: url, params: params });
        return next.handle(newReq);
    }

    private _getServerHeader(headerName: string): string | undefined {
        const providerValue = this._getOptionalStringProvider(headerName);
        if (providerValue) {
            return providerValue;
        }

        const headerValue = this._request?.get?.(headerName) ?? this._request?.headers?.[headerName.toLowerCase()];
        return this._toOptionalString(headerValue);
    }

    private _getOptionalStringProvider(tokenName: string): string | undefined {
        try {
            return this._toOptionalString(this._injector.get(tokenName, null));
        } catch {
            return undefined;
        }
    }

    private _toOptionalString(value: unknown): string | undefined {
        if (typeof value === 'string') {
            return value;
        }

        if (Array.isArray(value) && typeof value[0] === 'string') {
            return value[0];
        }

        return undefined;
    }
}
